import React, { useEffect, useState } from 'react';
import { Sparkles, Plus, Edit, Trash2, X, Save, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INTENSITY_LEVELS = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' }
];

const emptyForm = {
  name: '',
  description: '',
  how_to_apply: '',
  intensity_level: 'media'
};

const AdminTrainingTechniques = () => {
  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('session_token') || localStorage.getItem('token');

  const fetchTechniques = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/training-techniques`, { headers: { Authorization: `Bearer ${token}` } });
      setTechniques(res.data.items || []);
    } catch (err) {
      toast.error('Error al cargar las técnicas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTechniques(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...emptyForm, ...t });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/admin/training-techniques/${editing.technique_id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Técnica actualizada');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/training-techniques`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Técnica creada');
      }
      setModalOpen(false);
      fetchTechniques();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`¿Eliminar la técnica "${t.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/training-techniques/${t.technique_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Técnica eliminada');
      fetchTechniques();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const filtered = techniques.filter(t =>
    !search.trim() || (t.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const intensityBadge = (level) => {
    const map = {
      baja: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
      media: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
      alta: 'bg-red-500/15 text-red-300 border-red-500/30'
    };
    return map[level] || map.media;
  };

  return (
    <div className="min-h-screen bg-[#09090b] p-6 md:p-10" data-testid="admin-training-techniques">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-pink-400" />
              </div>
              <h1 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                Técnicas de <span className="text-pink-500">Entrenamiento</span>
              </h1>
            </div>
            <p className="text-zinc-400 text-sm">{techniques.length} técnicas registradas</p>
          </div>
          <Button onClick={openCreate} className="bg-pink-600 hover:bg-pink-500 rounded-full px-6 py-5 font-bold uppercase text-sm" data-testid="add-technique-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva técnica
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar técnica..."
            className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-full text-white text-sm focus:border-pink-500 outline-none"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-zinc-500">Cargando técnicas...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-3xl p-12 text-center">
            <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-1">No hay técnicas todavía</p>
            <p className="text-zinc-600 text-sm">Agrega técnicas como Drop Set, Super Set, Rest-Pause, etc.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map(t => (
              <div key={t.technique_id} className="bg-zinc-900/60 border border-zinc-800 hover:border-pink-500/40 rounded-2xl p-5 transition-colors" data-testid={`technique-card-${t.technique_id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-lg">{t.name}</h3>
                    <span className={`mt-1.5 inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${intensityBadge(t.intensity_level)}`}>
                      Intensidad {t.intensity_level}
                    </span>
                  </div>
                </div>
                {t.description && (
                  <p className="text-sm text-zinc-300 mb-3">{t.description}</p>
                )}
                {t.how_to_apply && (
                  <div className="bg-zinc-800/50 rounded-xl p-3 mb-3">
                    <p className="text-[11px] font-bold uppercase text-pink-400 tracking-wider mb-1">Cómo aplicar</p>
                    <p className="text-xs text-zinc-300">{t.how_to_apply}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
                  <button onClick={() => openEdit(t)} className="flex-1 text-xs font-bold uppercase text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-full py-2 transition-colors flex items-center justify-center gap-1.5" data-testid={`edit-technique-${t.technique_id}`}>
                    <Edit className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button onClick={() => handleDelete(t)} className="flex-1 text-xs font-bold uppercase text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-full py-2 transition-colors flex items-center justify-center gap-1.5" data-testid={`delete-technique-${t.technique_id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <h2 className="text-xl font-bold text-white">
                {editing ? 'Editar técnica' : 'Nueva técnica'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Nombre *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej. Drop Set"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none"
                  data-testid="technique-name-input"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Descripción</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Qué es esta técnica y para qué sirve..."
                  rows={3}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Cómo se aplica</label>
                <textarea
                  value={form.how_to_apply}
                  onChange={(e) => setForm({ ...form, how_to_apply: e.target.value })}
                  placeholder="Paso a paso de cómo ejecutar la técnica..."
                  rows={4}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Intensidad</label>
                <select value={form.intensity_level} onChange={(e) => setForm({ ...form, intensity_level: e.target.value })} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none">
                  {INTENSITY_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setModalOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full px-6">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-pink-600 hover:bg-pink-500 rounded-full px-8" data-testid="save-technique-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTrainingTechniques;
